import { buildClusterFaqJsonLdAsync } from "@/lib/clusterFaq";
import PlanningToolsLinks from "@/components/PlanningToolsLinks";
import { ServicePageEnhancer } from "@/components/service/ServicePageEnhancer";
import QuotableStatsStrip from "@/components/QuotableStatsStrip";
import AnswerBoxTop from "@/components/AnswerBoxTop";
import { HubCountryLinks } from "@/components/HubCountryLinks";
// EN mirror of /embassy — embassy liaison hub
import { createFileRoute, Link } from "@tanstack/react-router";
import { Building2, MapPin } from "lucide-react";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { claimReviewSchema, softwareApplicationSchema, entitySameAsSchema, COMMON_CLAIM_REBUTTALS_EN } from "@/lib/trust-schema";
import { RelatedHubGrid } from "@/components/RelatedHubGrid";

const SITE = "https://nyclanguageinstitute.com";
const TITLE = "Embassy Liaison Bangkok — Document Submission to 50+ Embassies | NYC Language Institute";
const DESC =
  "We deliver translated, MFA-legalized documents directly to embassies in Bangkok — Sathorn, Wireless. Get your timeline and checklist in minutes — chat now.";

const EMBASSIES = [
  "united-states", "united-kingdom", "germany", "france", "italy", "spain", "netherlands",
  "japan", "south-korea", "china", "india", "vietnam", "indonesia", "philippines",
  "australia", "new-zealand", "canada", "uae", "saudi-arabia", "russia",
];

export const Route = createFileRoute("/en/embassy/")({
  loader: async () => ({ clusterFaq: await buildClusterFaqJsonLdAsync("embassy", "en", "https://nyclanguageinstitute.com/en/embassy") }),
  head: ({ loaderData }: any) => ({
    meta: [
      { title: ctaTitle(TITLE, "/en/embassy", "en")},
      { name: "description", content: ctaDesc(DESC, "/en/embassy", "en")},
      { property: "og:title", content: ctaTitle(TITLE, "/en/embassy", "en")},
      { property: "og:description", content: ctaDesc(DESC, "/en/embassy", "en")},
      { property: "og:url", content: `${SITE}/en/embassy` },
      { property: "og:locale", content: "en_US" },
    ],
    links: [
      { rel: "canonical", href: `${SITE}/en/embassy` },
      ...hreflangLinks("/embassy", "/en/embassy"),
    ],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Service",
        name: "Embassy Liaison & Document Submission",
        provider: { "@type": "LegalService", name: "NYC Legal & Notary Service Co., Ltd.", url: SITE },
        areaServed: "TH",
        inLanguage: "en",
      }),
    },
    entitySameAsSchema(),
    softwareApplicationSchema({ url: `${SITE}/api/public/mcp`, name: "NYC Language Institute — MCP Server", description: "AI agent endpoint exposing embassy liaison tooling for 50+ embassies in Bangkok." }),
    claimReviewSchema(COMMON_CLAIM_REBUTTALS_EN(`${SITE}/en/embassy`)),
    
        ...(loaderData?.clusterFaq ? [{ type: "application/ld+json", children: JSON.stringify(loaderData.clusterFaq) }] : []),
      ],
  }),
  component: EnEmbassyHub,
});

function EnEmbassyHub() {
  return (
    <main className="min-h-screen bg-white">
      <section className="bg-navy-deep px-4 py-16 text-white">
        <div className="mx-auto max-w-5xl">
          <Link to="/en" className="text-xs text-amber-300 hover:underline">← EN home</Link>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">
            Embassy liaison — <span className="text-amber-300">{EMBASSIES.length}+ embassies</span>
          </h1>
          <p className="mt-4 max-w-2xl text-white/80">
            We translate, notarise, legalize at MFA, and walk your file into the destination embassy in Bangkok.
            You stay home; we return the legalized originals by courier.
          </p>
          <div className="quick-answer mt-6 max-w-2xl rounded-2xl border border-amber-400/40 bg-amber-500/10 p-5" data-speakable="true" itemScope itemType="https://schema.org/Question">
            <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-amber-300">AI Quick Answer</div>
            <h2 className="text-base font-bold text-white sm:text-lg" itemProp="name">Can someone submit documents to a Bangkok embassy on my behalf?</h2>
            <div itemProp="acceptedAnswer" itemScope itemType="https://schema.org/Answer">
              <p className="direct-answer mt-2 text-sm leading-relaxed text-white/85" itemProp="text">Yes. NYC Language Institute provides authorized embassy liaison service for 50+ embassies in Bangkok (Sathorn, Wireless Road, Witthayu). We handle translation, MFA legalization, and in-person embassy submission within 3–7 business days. Originals are returned via EMS or hand delivery — you never leave home.</p>
            </div>
          </div>
        </div>
      </section>
      <section className="px-4 py-14">
        <div className="mx-auto max-w-6xl">
          <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {EMBASSIES.map((slug) => (
              <Link key={slug} to="/embassy" className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm hover:border-amber-400">
                <Building2 className="h-4 w-4 text-amber-600" />
                <span className="font-medium capitalize text-navy-deep">{slug.replace(/-/g, " ")}</span>
              </Link>
            ))}
          </div>
          <div className="mt-10 rounded-2xl border border-slate-200 bg-slate-50 p-6 text-sm text-slate-700">
            <MapPin className="h-5 w-5 text-amber-600" />
            <p className="mt-2">
              Our HQ is 18 min from Sathorn, 22 min from Wireless Rd, 25 min from Witthayu — all major embassy
              districts. Same-day pickup + drop-off via in-house messenger team.
            </p>
          </div>
          <section className="mt-10">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep">Documents that must clear the MFA before the embassy</h2>
            <p className="mt-2 text-sm leading-7 text-slate-600">
              Every file submitted to an embassy in Bangkok must first be legalised by the Department of Consular
              Affairs, Ministry of Foreign Affairs. Read the step order and preparation checklist per document family.
              Sources: consular.mfa.go.th and HCCH (the Apostille Convention enters into force for Thailand on
              28 February 2027).
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              <Link to="/en/mfa-legalisation/documents" className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-navy-deep hover:border-amber-400">
                MFA legalisation documents — 6 families →
              </Link>
              <Link to="/en/notary/documents" className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-navy-deep hover:border-amber-400">
                Documents a Notarial Services Attorney can certify →
              </Link>
            </div>
          </section>

        <RelatedHubGrid locale="en" />
      </div>
      </section>
          <QuotableStatsStrip cluster="embassy" locale="en" variant="light" />
          <HubCountryLinks service="embassy" lang="en" />
      <ServicePageEnhancer serviceName="Embassy Legalization" locale="en" />

      <PlanningToolsLinks locale="en" />
      </main>
  );
}
